import { ampli } from '@/ampli';
import { useSignMessage } from '@privy-io/react-auth/solana';
import { getBase58Decoder } from '@solana/kit';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import type { WalletClient } from 'viem';
import { usePublicClient, useSwitchChain, useWalletClient } from 'wagmi';
import { getWalletClient } from 'wagmi/actions';

import { tokenFormatter } from '@/lib/formatting';
import { logger } from '@/lib/logger';

import { LimitOrderToast } from '@/components/TransactionToast/LimitOrderToast';

import { NETWORK_NAME_PER_CHAIN_ID, solana } from '@/constants/chain';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import type { LimitOrderItem } from '@/utils/0x.types';
import {
  cancelDefinitiveLimitOrder,
  cancelOneInchLimitOrder,
  invalidateOpenLimitOrders,
} from '@/utils/0x/orderbook';
import { config as wagmiConfig } from '@/utils/wagmi';
import { isUserRejection, trySwitchChain } from '@/utils/wallet';

type UseCancelLimitOrderOptions = {
  /**
   * Called after a cancellation is broadcast and the open-orders cache has been
   * invalidated. Used by surfaces that track open orders in their own store
   * (e.g. the history page) to drop the row.
   */
  onCancelSuccess?: () => void;
};

type CancellationWallet =
  | { family: 'evm'; walletClient: WalletClient }
  | { family: 'svm'; address: string; signMessage: (message: string) => Promise<string> };

async function cancelLimitOrderForProvider(
  item: LimitOrderItem,
  wallet: CancellationWallet,
): Promise<{ type: 'transaction'; hash: `0x${string}` } | { type: 'offchain' }> {
  switch (item.provider) {
    case '1inch': {
      if (wallet.family !== 'evm') {
        throw new Error('1inch limit orders require an EVM wallet');
      }
      return {
        type: 'transaction',
        hash: await cancelOneInchLimitOrder({
          order: item,
          chainId: item.chainId,
          walletClient: wallet.walletClient,
        }),
      };
    }
    case 'definitive': {
      const signMessage =
        wallet.family === 'svm'
          ? wallet.signMessage
          : async (message: string) => {
              const [account] = await wallet.walletClient.getAddresses();
              return wallet.walletClient.signMessage({ account, message });
            };
      await cancelDefinitiveLimitOrder({ order: item, signMessage });
      return { type: 'offchain' };
    }
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
}

/**
 * Shared on-chain cancel flow for a single open limit order: switches chains if
 * needed, submits the cancellation, fires analytics + a pending toast, waits for
 * the receipt, morphs that toast into a success (or error) toast, and
 * invalidates the open-orders cache. UI (button vs dropdown) lives in the
 * calling component.
 */
export function useCancelLimitOrder(
  item: LimitOrderItem,
  { onCancelSuccess }: UseCancelLimitOrderOptions = {},
): { isCancelling: boolean; cancelOrder: () => Promise<void> } {
  const { chainId } = item;
  const queryClient = useQueryClient();
  const publicClient = usePublicClient({ chainId });
  const { data: walletClient } = useWalletClient();
  const { solanaWallet } = useMatchaWallets();
  const { signMessage: signSolanaMessage } = useSignMessage();
  const { switchChainAsync } = useSwitchChain();
  const [isCancelling, setIsCancelling] = useState(false);

  const sellToken = item.makerTokenResult;
  const buyToken = item.takerTokenResult;
  const orderType = item.advancedOrderType ?? 'limit';

  const cancelOrder = async () => {
    let cancellationWallet: CancellationWallet;
    if (chainId === solana.id) {
      if (!solanaWallet) return;
      cancellationWallet = {
        family: 'svm',
        address: solanaWallet.address,
        signMessage: async (message) => {
          const { signature } = await signSolanaMessage({
            wallet: solanaWallet,
            message: new TextEncoder().encode(message),
          });
          if (signature.length !== 64) {
            throw new Error('Solana wallet returned an invalid message signature');
          }
          return getBase58Decoder().decode(signature);
        },
      };
    } else {
      if (!walletClient) return;
      let walletClientToUse = walletClient;
      if (walletClient.chain.id !== chainId) {
        const result = await trySwitchChain({ chainId, switchChainAsync });
        if (!result.success) return;

        // The hook-based walletClient won't update until the next render, so grab
        // a fresh one bound to the target chain.
        walletClientToUse = (await getWalletClient(wagmiConfig, {
          chainId,
        })) as typeof walletClient;
      }
      cancellationWallet = { family: 'evm', walletClient: walletClientToUse };
    }

    // Reused across the flow so the pending toast morphs in place into the
    // success/error toast (same sonner id) once the receipt resolves.
    let cancelToastId: string | number | undefined;

    try {
      setIsCancelling(true);

      const cancellation = await cancelLimitOrderForProvider(item, cancellationWallet);
      ampli.limitOrderCancelled({
        sell_token_address: sellToken?.address,
        sell_token_chain_id: chainId,
        sell_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[chainId],
        sell_token_symbol: sellToken?.symbol,
        buy_token_address: buyToken?.address,
        buy_token_chain_id: chainId,
        buy_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[chainId],
        buy_token_symbol: buyToken?.symbol,
        limit_order_provider: item.provider,
      });

      // Only broadcast here, not mined: a success toast would wrongly imply the
      // order is gone while it still shows as open. Show a pending toast instead.
      if (sellToken && buyToken) {
        cancelToastId = toast(
          <LimitOrderToast
            checkoutStep="cancelPending"
            orderType={orderType}
            sellAmount={tokenFormatter.format(item.makerAmount, { decimals: sellToken.decimals })}
            buyAmount={tokenFormatter.format(item.takerAmount, { decimals: buyToken.decimals })}
            sellToken={sellToken}
            buyToken={buyToken}
            onClose={() => toast.dismiss(cancelToastId)}
          />,
          { duration: Infinity, unstyled: true },
        );
      }

      if (cancellation.type === 'transaction') {
        if (!publicClient) {
          throw new Error('Public client is unavailable');
        }
        await publicClient.waitForTransactionReceipt({ hash: cancellation.hash });
      }
      const makerAddress =
        cancellationWallet.family === 'svm'
          ? cancellationWallet.address
          : (await cancellationWallet.walletClient.getAddresses())[0];

      if (sellToken && buyToken) {
        cancelToastId = toast(
          <LimitOrderToast
            checkoutStep="success"
            orderType={orderType}
            sellAmount={tokenFormatter.format(item.makerAmount, { decimals: sellToken.decimals })}
            buyAmount={tokenFormatter.format(item.takerAmount, { decimals: buyToken.decimals })}
            sellToken={sellToken}
            buyToken={buyToken}
            onClose={() => toast.dismiss(cancelToastId)}
          />,
          { id: cancelToastId, duration: 6000, unstyled: true },
        );
      }

      // Cache invalidation is best-effort: a failure here shouldn't block the
      // caller's own cleanup (e.g. removing the row from its store).
      try {
        await invalidateOpenLimitOrders(queryClient, makerAddress, [item]);
      } catch (err) {
        logger.warn('Failed to invalidate open limit orders after cancellation', err);
      }

      onCancelSuccess?.();
    } catch (err) {
      // The user deliberately rejecting the wallet prompt isn't a failure worth
      // alerting on; only surface a toast for genuine errors so the cancel no
      // longer fails silently (MAT-6852).
      if (!isUserRejection(err) && sellToken && buyToken) {
        logger.warn('Failed to cancel open limit order', err);

        cancelToastId = toast(
          <LimitOrderToast
            checkoutStep="error"
            orderType={orderType}
            sellAmount={tokenFormatter.format(item.makerAmount, { decimals: sellToken.decimals })}
            buyAmount={tokenFormatter.format(item.takerAmount, { decimals: buyToken.decimals })}
            sellToken={sellToken}
            buyToken={buyToken}
            onClose={() => toast.dismiss(cancelToastId)}
          />,
          { id: cancelToastId, duration: 6000, unstyled: true },
        );
      } else if (cancelToastId !== undefined) {
        // Clear the infinite-duration pending toast on a non-toastable error.
        toast.dismiss(cancelToastId);
      }
    } finally {
      setIsCancelling(false);
    }
  };

  return { isCancelling, cancelOrder };
}
